import { Injectable, signal } from '@angular/core';

export interface TicketDashboardHeaderState {
  rldcOptions: { id: number; label: string }[];
  rldcId: number | null;
  dateRange: Date[] | null;
}

interface TicketDashboardHeaderHandlers {
  rldcChange: (value: number | null) => void;
  dateRangeChange: (value: Date[] | null) => void;
  apply: () => void;
  reset: () => void;
}

@Injectable({ providedIn: 'root' })
export class TicketDashboardHeaderService {
  private readonly headerState = signal<TicketDashboardHeaderState | null>(null);
  private connection: symbol | null = null;
  private handlers: TicketDashboardHeaderHandlers | null = null;

  readonly state = this.headerState.asReadonly();

  connect(state: TicketDashboardHeaderState, handlers: TicketDashboardHeaderHandlers): symbol {
    const connection = Symbol('ticket-dashboard-header');
    this.connection = connection;
    this.handlers = handlers;
    this.headerState.set(state);
    return connection;
  }

  sync(connection: symbol, state: TicketDashboardHeaderState): void {
    if (connection === this.connection) this.headerState.set(state);
  }

  disconnect(connection: symbol): void {
    if (connection !== this.connection) return;
    this.connection = null;
    this.handlers = null;
    this.headerState.set(null);
  }

  changeRldc(value: number | null): void {
    this.patchState({ rldcId: value });
    this.handlers?.rldcChange(value);
  }

  changeDateRange(value: Date[] | null): void {
    this.patchState({ dateRange: value });
    this.handlers?.dateRangeChange(value);
  }

  apply(): void {
    this.handlers?.apply();
  }

  reset(): void {
    this.handlers?.reset();
  }

  private patchState(patch: Partial<TicketDashboardHeaderState>): void {
    const state = this.headerState();
    if (state) this.headerState.set({ ...state, ...patch });
  }
}
